import { z } from "zod";

import { COUNTRY_CODES, STATE_PROVINCE_CODES } from "@examspark/common";

export const AddressSchema = z
  .object({
    city: z.string(),
    state: z.enum(STATE_PROVINCE_CODES),
    country: z.enum(COUNTRY_CODES),
  })
  .refine(
    (arg) => arg.state.startsWith(arg.country),
    "Specified state must be within specified country",
  );

export const ApiV1LicenseSchema = z.object({
  products: z
    .array(
      z
        .object({
          bank: z.object({
            id: z.string(),
          }),
        })
        .or(
          z.object({
            course: z
              .object({ id: z.string() })
              .or(z.object({ vendorId: z.string() })),
          }),
        ),
    )
    .optional(),
  appId: z.string().optional(),
});

// temporarily allow license properties at the top of the object
// to support migration for focus
export const ApiV1LicenseSchemaBackwardsCompatible = ApiV1LicenseSchema.and(
  z.object({ license: ApiV1LicenseSchema.optional() }),
).transform((arg) => {
  if ((arg.appId != null || arg.products != null) && arg.license == null) {
    arg.license = { appId: arg.appId, products: arg.products };
  }

  return { license: arg.license } as {
    license?: z.infer<typeof ApiV1LicenseSchema>;
  };
});

export const ApiV1UpdateWorkspaceRequestSchema = z
  .object({
    name: z.string(),
    ncesId: z.string().optional(),
    type: z.enum(["CAMPUS", "DISTRICT"]).optional(),
    /**
     * `undefined` means its neither setting nor deleting; `null` explicitly
     * means signals to delete the parent relationship
     */
    parentWorkspaceId: z.string().optional().nullable(),
    requireChildWorkspaceSelection: z.boolean().optional(),
    address: AddressSchema,
    objectiveDocuments: z.array(z.string()).optional(),
  })
  .and(ApiV1LicenseSchemaBackwardsCompatible);

export const ApiV1CreateWorkspaceBasicRequestSchema = z.object({
  name: z.string(),
  id: z.string().optional(),
  ncesId: z.string().optional(),
  type: z.enum(["CAMPUS", "DISTRICT"]).optional(),
  /**
   * `undefined` means its neither setting nor deleting; `null` explicitly
   * means signals to delete the parent relationship
   */
  parentWorkspaceId: z.string().optional().nullable(),
  requireChildWorkspaceSelection: z.boolean().optional(),
  address: AddressSchema,
  objectiveDocuments: z.array(z.string()).optional(),
});

export const ApiV1CreateWorkspaceRequestSchema = z
  .object({
    name: z.string(),
    id: z.string().optional(),
    ncesId: z.string().optional(),
    type: z.enum(["CAMPUS", "DISTRICT"]).optional(),
    /**
     * `undefined` means its neither setting nor deleting; `null` explicitly
     * means signals to delete the parent relationship
     */
    parentWorkspaceId: z.string().optional().nullable(),
    requireChildWorkspaceSelection: z.boolean().optional(),
    address: AddressSchema,
    objectiveDocuments: z.array(z.string()).optional(),
  })
  .and(ApiV1LicenseSchemaBackwardsCompatible);

export const ApiV1PutWorkspaceUserRequestSchema = z.object({
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  license: ApiV1LicenseSchema.optional(),
});
